/**
 * @fileoverview Authentication API service
 * Handles all authentication-related HTTP requests using existing API infrastructure
 * @author SKEMA CANADA
 */

import { apiPost, apiGet, handleApiError, getApiConfig } from "@services/api/apiConfig";

/**
 * Authentication API endpoints
 * @constant {Object}
 */
const AUTH_ENDPOINTS = {
  LOGIN: "/auth/login",
  LOGOUT: "/auth/logout",
  ME: "/auth/me",
  REFRESH: "/auth/refresh",
};

/**
 * Authentication API methods
 */
export const authApi = {
  /**
   * Authenticate user with email and password.
   * The JWT is returned as an HttpOnly cookie by the server.
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response with user data
   * @throws {Error} Authentication error with user-friendly message
   */
  login: async (credentials) => {
    try {
      const response = await apiPost(AUTH_ENDPOINTS.LOGIN, credentials);

      if (!response.user || !response.user.id) {
        throw new Error("Invalid server response: missing user data");
      }

      return {
        user: {
          id: response.user.id,
          username: response.user.username,
          email: response.user.email,
        },
      };
    } catch (error) {
      handleApiError(error, "User login");
    }
  },

  /**
   * Log out the current user by clearing the server-side cookie.
   * @returns {Promise<Object>} Logout response
   * @throws {Error} Logout error with user-friendly message
   */
  logout: async () => {
    try {
      const response = await apiPost(AUTH_ENDPOINTS.LOGOUT);

      return {
        success: true,
        message: response.message || "Logged out successfully",
      };
    } catch (error) {
      handleApiError(error, "User logout");
    }
  },

  /**
   * Verify the current session by calling the protected /me endpoint.
   * The HttpOnly cookie is sent automatically by the browser.
   * @returns {Promise<Object>} Current user data
   * @throws {Error} Verification error if session is invalid
   */
  verifySession: async () => {
    try {
      const response = await apiGet(AUTH_ENDPOINTS.ME);

      if (!response.id) {
        throw new Error("Authentication verification failed");
      }

      return {
        user: {
          id: response.id,
          username: response.username,
          email: response.email,
        },
      };
    } catch (error) {
      handleApiError(error, "Session verification");
    }
  },

  /**
   * Refresh the current session by requesting a new JWT from the server.
   * Uses a direct fetch call to bypass the 401 interceptor and avoid infinite loops.
   * @returns {Promise<Object>} Refreshed user data
   * @throws {Error} If the session cannot be refreshed
   */
  refreshSession: async () => {
    const config = getApiConfig();
    const url = `${config.baseURL}${AUTH_ENDPOINTS.REFRESH}`;

    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: config.headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.user || !data.user.id) {
      throw new Error("Invalid refresh response");
    }

    return {
      user: {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email,
      },
    };
  },
};

/**
 * Authentication-specific error types for enhanced error handling
 * @constant {Object}
 */
export const AUTH_ERRORS = {
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  TOKEN_INVALID: "TOKEN_INVALID",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  PERMISSION_DENIED: "PERMISSION_DENIED",
};

/**
 * Parse authentication-specific API errors
 * @param {Error} error - Original error from API
 * @returns {Object} Enhanced error object with auth-specific context
 */
export const parseAuthError = (error) => {
  const message = error.message?.toLowerCase() || "";

  if (message.includes("invalid credentials") || message.includes("401")) {
    return {
      type: AUTH_ERRORS.INVALID_CREDENTIALS,
      message: "Invalid email or password",
      originalError: error,
    };
  }

  if (
    message.includes("session expired") ||
    message.includes("token expired")
  ) {
    return {
      type: AUTH_ERRORS.SESSION_EXPIRED,
      message: "Your session has expired. Please log in again.",
      originalError: error,
    };
  }

  if (
    message.includes("invalid token") ||
    message.includes("malformed token")
  ) {
    return {
      type: AUTH_ERRORS.TOKEN_INVALID,
      message: "Authentication token is invalid. Please log in again.",
      originalError: error,
    };
  }

  if (
    message.includes("account locked") ||
    message.includes("account suspended")
  ) {
    return {
      type: AUTH_ERRORS.ACCOUNT_LOCKED,
      message: "Your account has been locked. Please contact support.",
      originalError: error,
    };
  }

  if (message.includes("403") || message.includes("forbidden")) {
    return {
      type: AUTH_ERRORS.PERMISSION_DENIED,
      message: "You do not have permission to perform this action.",
      originalError: error,
    };
  }

  return {
    type: "UNKNOWN_AUTH_ERROR",
    message: error.message || "An authentication error occurred",
    originalError: error,
  };
};

/**
 * Check if error is authentication-related and requires re-login
 * @param {Error} error - Error to check
 * @returns {boolean} Whether error requires authentication
 */
export const requiresReAuthentication = (error) => {
  const authError = parseAuthError(error);

  return [
    AUTH_ERRORS.INVALID_CREDENTIALS,
    AUTH_ERRORS.SESSION_EXPIRED,
    AUTH_ERRORS.TOKEN_INVALID,
  ].includes(authError.type);
};
